import React, { useState } from 'react';
import { Bell, BookOpen, Briefcase, Calendar, ChevronDown, Globe, Home, LogOut, Mail, Menu, MessageCircle, Moon, Search, Settings, Star, Sun, User, Users, X } from 'lucide-react';

const App = () => {
  const [darkMode, setDarkMode] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentTab, setCurrentTab] = useState('home');
  const [notifications, setNotifications] = useState(7);
  const [messages, setMessages] = useState(3);
  const [language, setLanguage] = useState('English');
  const [searchQuery, setSearchQuery] = useState('');

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  // Main navigation items
  const navItems = [
    { id: 'home', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
    { id: 'community', label: 'Community & Discussions', icon: <Users className="w-5 h-5" /> },
    { id: 'career', label: 'Career & Opportunities', icon: <Briefcase className="w-5 h-5" /> },
    { id: 'learning', label: 'Learning & Growth', icon: <BookOpen className="w-5 h-5" /> },
    { id: 'gamification', label: 'Gamification & Recognition', icon: <Star className="w-5 h-5" /> },
  ];

  return (
    <div className={`flex h-screen ${darkMode ? 'dark bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 lg:hidden z-20" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed lg:relative z-30 w-64 h-full transition-all duration-300 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 ${darkMode ? 'bg-gray-800' : 'bg-white'} border-r ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex flex-col h-full">
          {/* Logo and close button */}
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-md ${darkMode ? 'bg-indigo-600' : 'bg-indigo-500'} flex items-center justify-center`}>
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl">EduConnect</span>
            </div>
            <button 
              className="p-1 rounded-md lg:hidden hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation items */}
          <div className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.id}>
                  <button
                    className={`flex items-center w-full px-4 py-3 text-left rounded-lg transition-colors ${
                      currentTab === item.id
                        ? darkMode
                          ? 'bg-gray-700 text-white'
                          : 'bg-indigo-100 text-indigo-700'
                        : darkMode
                        ? 'hover:bg-gray-700'
                        : 'hover:bg-gray-100'
                    }`}
                    onClick={() => setCurrentTab(item.id)}
                  >
                    <div className="mr-3">{item.icon}</div>
                    <span className="font-medium">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <div className={`px-4 py-2 text-xs uppercase font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Your Account
              </div>
              <ul className="mt-2 space-y-2">
                <li>
                  <a href="#" className={`flex items-center px-4 py-3 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                    <User className="w-5 h-5 mr-3" />
                    <span className="font-medium">Profile</span>
                  </a>
                </li>
                <li>
                  <a href="#" className={`flex items-center px-4 py-3 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                    <Settings className="w-5 h-5 mr-3" />
                    <span className="font-medium">Settings</span>
                  </a>
                </li>
                <li>
                  <a href="#" className={`flex items-center px-4 py-3 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                    <LogOut className="w-5 h-5 mr-3" />
                    <span className="font-medium">Log Out</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className={`flex items-center justify-between p-4 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b`}>
          {/* Left side - Menu button and search */}
          <div className="flex items-center gap-4">
            <button
              className="p-2 rounded-md lg:hidden hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className={`relative rounded-md shadow-sm ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
              <input
                type="text"
                className={`block w-full py-2 pl-10 pr-3 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'
                }`}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Right side - User menu and options */}
          <div className="flex items-center space-x-4">
            {/* Language selector */}
            <div className="relative">
              <button
                className={`flex items-center p-2 rounded-md ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <Globe className="w-5 h-5 mr-1" />
                <span className="hidden md:inline text-sm font-medium">{language}</span>
                <ChevronDown className="w-4 h-4 ml-1" />
              </button>
            </div>

            {/* Dark mode toggle */}
            <button
              className={`p-2 rounded-md ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              onClick={toggleDarkMode}
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* Messages */}
            <button className={`p-2 rounded-md ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} relative`}>
              <Mail className="w-5 h-5" />
              {messages > 0 && (
                <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full">
                  {messages}
                </span>
              )}
            </button>

            {/* Notifications */}
            <button className={`p-2 rounded-md ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} relative`}>
              <Bell className="w-5 h-5" />
              {notifications > 0 && (
                <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full">
                  {notifications}
                </span>
              )}
            </button>

            {/* User profile */}
            <div className="relative">
              <button className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white font-medium">
                  G
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-medium"></div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}></div>
                </div>
                <ChevronDown className="w-4 h-4 hidden md:block" />
              </button>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className={`flex-1 overflow-y-auto p-6 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          {/* Content for current tab will be rendered here */}
          <DashboardContent currentTab={currentTab} darkMode={darkMode} />
        </main>
      </div>
    </div>
  );
};

const DashboardContent = ({ currentTab, darkMode }) => {
  const getTabContent = () => {
    switch (currentTab) {
      case 'home':
        return <HomeTab darkMode={darkMode} />;
      case 'community':
        return <CommunityTab darkMode={darkMode} />;
      case 'career':
        return <CareerTab darkMode={darkMode} />;
      case 'learning':
        return <LearningTab darkMode={darkMode} />;
      case 'gamification':
        return <GamificationTab darkMode={darkMode} />;
      default:
        return <HomeTab darkMode={darkMode} />;
    }
  };

  return getTabContent();
};

const HomeTab = ({ darkMode }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <button className={`px-4 py-2 rounded-md ${darkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'} text-white`}>
          Customize Dashboard
        </button>
      </div>

      {/* Dashboard widgets grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Upcoming events widget */}
        <div className={`rounded-lg shadow-md overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="font-semibold text-lg flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Upcoming Events
            </h2>
          </div>
          <div className="p-4">
            <ul className="space-y-3">
              {[
                { title: "AI Workshop", date: "Mar 10", type: "Workshop" },
                { title: "Career Fair", date: "Mar 15", type: "Career" },
                { title: "Hackathon", date: "Mar 20", type: "Event" }
              ].map((event, index) => (
                <li key={index} className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{event.title}</div>
                    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{event.date}</div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    event.type === 'Workshop' 
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                      : event.type === 'Career' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                  }`}>
                    {event.type}
                  </span>
                </li>
              ))}
            </ul>
            <a href="#" className={`mt-4 block text-center py-2 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} text-sm font-medium`}>
              View All Events
            </a>
          </div>
        </div>

        {/* Recent discussions widget */}
        <div className={`rounded-lg shadow-md overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="font-semibold text-lg flex items-center">
              <MessageCircle className="w-5 h-5 mr-2" />
              Recent Discussions
            </h2>
          </div>
          <div className="p-4">
            <ul className="space-y-3">
              {[
                { title: "Tips for landing internships?", replies: 23, category: "Career" },
                { title: "Best resources for machine learning", replies: 15, category: "Learning" },
                { title: "Study group for algorithms", replies: 8, category: "Groups" }
              ].map((discussion, index) => (
                <li key={index} className={`p-2 rounded-md ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                  <div className="font-medium">{discussion.title}</div>
                  <div className="flex items-center justify-between mt-1">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      discussion.category === 'Career' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : discussion.category === 'Learning' 
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                    }`}>
                      {discussion.category}
                    </span>
                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{discussion.replies} replies</span>
                  </div>
                </li>
              ))}
            </ul>
            <a href="#" className={`mt-4 block text-center py-2 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} text-sm font-medium`}>
              View All Discussions
            </a>
          </div>
        </div>

        {/* Study progress widget */}
        <div className={`rounded-lg shadow-md overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="font-semibold text-lg flex items-center">
              <BookOpen className="w-5 h-5 mr-2" />
              Learning Progress
            </h2>
          </div>
          <div className="p-4">
            <div className="space-y-4">
              {[
                { course: "Machine Learning", progress: 75 },
                { course: "Web Development", progress: 60 },
                { course: "Data Structures", progress: 90 }
              ].map((course, index) => (
                <div key={index}>
                  <div className="flex justify-between mb-1">
                    <span className="font-medium">{course.course}</span>
                    <span>{course.progress}%</span>
                  </div>
                  <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div 
                      className="h-2 rounded-full bg-indigo-500" 
                      style={{ width: `${course.progress}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
            <a href="#" className={`mt-6 block text-center py-2 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} text-sm font-medium`}>
              View All Courses
            </a>
          </div>
        </div>
      </div>

      {/* Recent notifications */}
      <div className={`rounded-lg shadow-md overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="font-semibold text-lg flex items-center">
            <Bell className="w-5 h-5 mr-2" />
            Recent Notifications
          </h2>
        </div>
        <div className="p-4">
          <ul className="space-y-3">
            {[
              { title: "Your application at Google has been viewed", time: "2 hours ago", type: "Career" },
              { title: "New scholarship opportunity matches your profile", time: "5 hours ago", type: "Scholarship" },
              { title: "Sarah commented on your question", time: "Yesterday", type: "Discussion" },
              { title: "Upcoming deadline: Submit project by Friday", time: "Yesterday", type: "Reminder" },
            ].map((notification, index) => (
              <li key={index} className={`p-3 rounded-md ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} flex items-start`}>
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                  notification.type === 'Career' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                  notification.type === 'Scholarship' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' :
                  notification.type === 'Discussion' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                }`}>
                  {notification.type === 'Career' && <Briefcase className="w-4 h-4" />}
                  {notification.type === 'Scholarship' && <Star className="w-4 h-4" />}
                  {notification.type === 'Discussion' && <MessageCircle className="w-4 h-4" />}
                  {notification.type === 'Reminder' && <Calendar className="w-4 h-4" />}
                </div>
                <div className="flex-1">
                  <div className="font-medium">{notification.title}</div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{notification.time}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};


const CommunityTab = ({ darkMode }) => {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Community & Discussions</h1>
      {/* Q&A Forums Section */}
      <div className={`rounded-lg shadow-md overflow-hidden mb-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="font-semibold text-lg">Q&A Forums</h2>
          <button className={`px-4 py-2 rounded-md ${darkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'} text-white text-sm`}>
            Ask a Question
          </button>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className={`px-3 py-1 rounded-full text-sm ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>All Questions</span>
            <span className={`px-3 py-1 rounded-full text-sm ${darkMode ? 'bg-indigo-900 text-indigo-200' : 'bg-indigo-100 text-indigo-800'}`}>Computer Science</span>
            <span className={`px-3 py-1 rounded-full text-sm ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>Mathematics</span>
            <span className={`px-3 py-1 rounded-full text-sm ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>Physics</span>
            <span className={`px-3 py-1 rounded-full text-sm ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>Career</span>
          </div>
          
          <div className="space-y-4">
            {[
              { title: "What's the best approach to learning algorithms?", votes: 42, answers: 15, tags: ["Algorithms", "Learning"] },
              { title: "How do I prepare for technical interviews at FAANG?", votes: 38, answers: 23, tags: ["Career", "Interviews"] },
              { title: "Resources for learning React and TypeScript in 2025", votes: 29, answers: 11, tags: ["Web Development", "React"] },
            ].map((question, index) => (
              <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <button className={`w-8 h-8 rounded-md ${darkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'} flex items-center justify-center`}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <span className="my-1 font-medium">{question.votes}</span>
                    <button className={`w-8 h-8 rounded-md ${darkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'} flex items-center justify-center`}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-lg mb-2">{question.title}</h3>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {question.tags.map((tag, i) => (
                        <span key={i} className={`px-2 py-1 rounded-full text-xs ${
                          darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                        }`}>
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span>{question.answers} answers</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs">
                          JD
                        </div>
                        <span className="ml-2 text-sm"> Muwango IBRAH</span>
                        <span className={`ml-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>2 days ago</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <a href="#" className={`mt-4 block text-center py-2 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} text-sm font-medium`}>
            View All Questions
          </a>
        </div>
      </div>
      
      {/* Study Groups Section */}
      <div className={`rounded-lg shadow-md overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="font-semibold text-lg">Study Groups & Communities</h2>
          <button className={`px-4 py-2 rounded-md ${darkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'} text-white text-sm`}>
            Create Group
          </button>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { name: "Algorithms Study Group", members: 78, university: "MIT", lastActive: "2 hours ago" },
              { name: "Machine Learning Enthusiasts", members: 156, university: "Stanford", lastActive: "1 hour ago" },
              { name: "Web Development Club", members: 92, university: "Berkeley", lastActive: "Just now" },
              { name: "Competitive Programming", members: 64, university: "CMU", lastActive: "3 hours ago" },
            ].map((group, index) => (
              <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} flex flex-col`}>
                <div className="font-medium text-lg mb-2">{group.name}</div>
                <div className="flex items-center mb-2">
                  <Users className="w-4 h-4 mr-1" />
                  <span className="text-sm">{group.members} members</span>
                </div>
                <div className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {group.university} • Active {group.lastActive}
                </div>
                <button className={`mt-auto px-4 py-2 rounded-md text-sm border ${
                  darkMode 
                    ? 'border-indigo-600 text-indigo-400 hover:bg-indigo-900 hover:bg-opacity-30' 
                    : 'border-indigo-500 text-indigo-600 hover:bg-indigo-50'
                }`}>
                  Join Group
                </button>
              </div>
            ))}
          </div>
          
          <a href="#" className={`mt-4 block text-center py-2 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} text-sm font-medium`}>
            View All Groups
          </a>
        </div>
      </div>
    </div>
  );
};

const CareerTab = ({ darkMode }) => {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Career & Opportunities</h1>
      
      {/* Job Portal Section */}
      <div className={`rounded-lg shadow-md overflow-hidden mb-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="font-semibold text-lg">Internship & Job Portal</h2>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between mb-6">
            <div className={`relative flex-1 max-w-md rounded-md shadow-sm ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
              <input
                type="text"
                className={`block w-full py-2 pl-10 pr-3 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'
                }`}
                placeholder="Search jobs..."
              />
            </div>
            <div className="flex items-center gap-3">
              <button className={`px-3 py-2 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} text-sm font-medium flex items-center`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filter
              </button>
              <button className={`px-4 py-2 rounded-md ${darkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'} text-white text-sm`}>
                AI Job Match
              </button>
            </div>
          </div>
          
          <div className="space-y-4">
            {[
              { 
                title: "Software Engineer Intern", 
                company: "Google", 
                location: "Mountain View, CA", 
                type: "Internship",
                remote: true,
                posted: "2 days ago",
                salary: "UGX8,000 - UGX10,000/month",
                skills: ["Python", "JavaScript", "React"]
              },
              { 
                title: "Frontend Developer", 
                company: "Microsoft", 
                location: "Seattle, WA", 
                type: "Full-time",
                remote: false,
                posted: "1 week ago",
                salary: "UGX120,000 - UGX150,000/year",
                skills: ["JavaScript", "TypeScript", "React"]
              },
              { 
                title: "Data Science Intern", 
                company: "Amazon", 
                location: "New York, NY", 
                type: "Internship",
                remote: true,
                posted: "3 days ago",
                salary: "UGX7,500 - UGX9,000/month",
                skills: ["Python", "SQL", "Machine Learning"]
              },
            ].map((job, index) => (
              <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-start">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <span className="font-bold text-xl">{job.company.charAt(0)}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap justify-between">
                      <h3 className="font-medium text-lg">{job.title}</h3>
                      <div className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className={`ml-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Save</span>
                      </div>
                    </div>
                    <div className="text-sm mb-2">{job.company} • {job.location} {job.remote && "• Remote"}</div>
                    <div className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{job.salary}</div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {job.skills.map((skill, i) => (
                        <span key={i} className={`px-2 py-1 rounded-full text-xs ${
                          darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                        }`}>
                          {skill}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Posted {job.posted}</span>
                      <div>
                        <button className={`px-4 py-2 rounded-md mr-2 text-sm ${
                          darkMode 
                            ? 'bg-gray-600 hover:bg-gray-500' 
                            : 'bg-gray-200 hover:bg-gray-300'
                        }`}>
                          View Details
                        </button>
                        <button className={`px-4 py-2 rounded-md text-sm ${
                          darkMode 
                            ? 'bg-indigo-600 hover:bg-indigo-700' 
                            : 'bg-indigo-500 hover:bg-indigo-600'
                        } text-white`}>
                          Apply Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <a href="#" className={`mt-4 block text-center py-2 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} text-sm font-medium`}>
            View All Jobs
          </a>
        </div>
      </div>
      
      {/* Mentorship Section */}
      <div className={`rounded-lg shadow-md overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="font-semibold text-lg">Mentorship & Orientation</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`rounded-lg p-4 ${darkMode ? 'bg-indigo-900 bg-opacity-30' : 'bg-indigo-50'} border ${darkMode ? 'border-indigo-700' : 'border-indigo-100'}`}>
              <h3 className="font-medium text-lg mb-3">Find a Mentor</h3>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Connect with industry professionals and senior students for guidance in your career journey.
              </p>
              <button className={`px-4 py-2 w-full rounded-md text-sm ${darkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'} text-white`}>
                Browse Mentors
              </button>
            </div>
            
            <div className={`rounded-lg p-4 ${darkMode ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50'} border ${darkMode ? 'border-purple-700' : 'border-purple-100'}`}>
              <h3 className="font-medium text-lg mb-3">Career Path Recommendations</h3>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Get AI-powered career path suggestions based on your skills, interests, and academic performance.
              </p>
              <button className={`px-4 py-2 w-full rounded-md text-sm ${darkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white`}>
                Get Recommendations
              </button>
            </div>
            
            <div className={`rounded-lg p-4 ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'} border ${darkMode ? 'border-blue-700' : 'border-blue-100'}`}>
              <h3 className="font-medium text-lg mb-3">Upcoming Career Events</h3>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Virtual career fairs, networking events, and industry panels to expand your professional network.
              </p>
              <button className={`px-4 py-2 w-full rounded-md text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white`}>
                View Events
              </button>
            </div>
          </div>
          
          <div className="mt-6">
            <h3 className="font-medium text-lg mb-4">Featured Mentors</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: "Fathedepp SINGH DHILLON", role: "Detective at FBI", expertise: ["AI/ML", "Career Growth"], avatar: "SJ" },
                { name: "BWIRE JACKSON", role: "Product Manager at SecDel", expertise: ["Product Management", "UX Design"], avatar: "MC" },
                { name: "MAGALA GRAHAM", role: "Security analysis at FOX", expertise: ["Data Science", "Machine Learning"], avatar: "JW" }
              ].map((mentor, index) => (
                <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-start">
                    <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-medium mr-3">
                      {mentor.avatar}
                    </div>
                    <div>
                      <h4 className="font-medium">{mentor.name}</h4>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{mentor.role}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {mentor.expertise.map((skill, i) => (
                          <span key={i} className={`px-2 py-1 rounded-full text-xs ${
                            darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                          }`}>
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <button className={`mt-3 px-4 py-2 w-full rounded-md text-sm ${
                    darkMode 
                      ? 'border border-indigo-600 text-indigo-400 hover:bg-indigo-900 hover:bg-opacity-30' 
                      : 'border border-indigo-500 text-indigo-600 hover:bg-indigo-50'
                  }`}>
                    Request Mentorship
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LearningTab = ({ darkMode }) => {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Learning & Growth</h1>
      
      {/* E-Learning Section */}
      <div className={`rounded-lg shadow-md overflow-hidden mb-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="font-semibold text-lg">E-Learning & Resources</h2>
          <button className={`px-4 py-2 rounded-md ${darkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'} text-white text-sm`}>
            Upload Resource
          </button>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {[
              { title: "Machine Learning Fundamentals", provider: "Coursera", level: "Intermediate", rating: 4.8, enrolled: 2530 },
              { title: "Web Development Bootcamp", provider: "Udemy", level: "Beginner", rating: 4.6, enrolled: 3245 },
              { title: "Data Structures & Algorithms", provider: "edX", level: "Advanced", rating: 4.9, enrolled: 1890 }
            ].map((course, index) => (
              <div key={index} className={`rounded-lg overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="h-36 bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center">
                  <BookOpen className="w-12 h-12 text-white" />
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-lg mb-1">{course.title}</h3>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{course.provider}</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                    }`}>{course.level}</span>
                  </div>
                  <div className="flex items-center mb-3">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${i < Math.floor(course.rating) ? 'text-yellow-500' : 'text-gray-300'}`} viewBox="0 0 20 20" fill="currentColor">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <span className={`ml-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{course.rating} • {course.enrolled.toLocaleString()} enrolled</span>
                  </div>
                  <button className={`w-full py-2 rounded-md text-sm ${
                    darkMode 
                      ? 'bg-indigo-600 hover:bg-indigo-700' 
                      : 'bg-indigo-500 hover:bg-indigo-600'
                  } text-white`}>
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <h3 className="font-medium text-lg mb-4">Recently Shared Resources</h3>
          <div className="space-y-3">
            {[
              { type: "PDF", title: "Advanced Algorithms Lecture Notes", size: "3.2 MB", author: "Prof. Johnson", downloads: 156 },
              { type: "Video", title: "Database Systems Tutorial", size: "230 MB", author: "Sarah Williams", downloads: 89 },
              { type: "Code", title: "React Components Library", size: "1.8 MB", author: "Mike Chen", downloads: 213 }
            ].map((resource, index) => (
              <div key={index} className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} flex items-center`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${
                  resource.type === 'PDF' 
                    ? 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300' 
                    : resource.type === 'Video'
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                    : 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300'
                }`}>
                  {resource.type === 'PDF' && (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  )}
                  {resource.type === 'Video' && (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  {resource.type === 'Code' && (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-medium">{resource.title}</div>
                  <div className="flex items-center justify-between">
                    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {resource.author} • {resource.size}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {resource.downloads} downloads
                    </div>
                  </div>
                </div>
                <button className={`ml-4 px-3 py-1 rounded-md text-sm ${
                  darkMode 
                    ? 'bg-gray-600 hover:bg-gray-500' 
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}>
                  Download
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Scholarship Section */}
      <div className={`rounded-lg shadow-md overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="font-semibold text-lg">Scholarship & Grant Finder</h2>
        </div>
        <div className="p-4">
          <div className={`p-4 mb-6 rounded-lg ${darkMode ? 'bg-indigo-900 bg-opacity-20' : 'bg-indigo-50'} border ${darkMode ? 'border-indigo-800' : 'border-indigo-100'}`}>
            <h3 className="font-medium text-lg mb-2">Personalized Scholarship Recommendations</h3>
            <p className="mb-4">Based on your profile, we've found 12 scholarships you may be eligible for.</p>
            <button className={`px-4 py-2 rounded-md text-sm ${darkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'} text-white`}>
              View Matches
            </button>
          </div>
          
          <h3 className="font-medium text-lg mb-4">Latest Scholarships</h3>
          <div className="space-y-4">
            {[
              { 
                name: "Global Tech Leaders Scholarship", 
                amount: "$10,000", 
                deadline: "April 15, 2025",
                eligibility: ["Computer Science", "Engineering", "GPA 3.5+"],
                location: "United States"
              },
              { 
                name: "Women in STEM Grant", 
                amount: "$5,000", 
                deadline: "May 1, 2025",
                eligibility: ["Female Students", "STEM Fields", "Undergraduate"],
                location: "Worldwide"
              },
              { 
                name: "Future Innovators Fund", 
                amount: "$7,500", 
                deadline: "March 30, 2025",
                eligibility: ["All Majors", "Innovation Project", "Junior/Senior"],
                location: "Europe, North America"
              },
            ].map((scholarship, index) => (
              <div key={index} className={`p-4 rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-lg">{scholarship.name}</h4>
                    <div className="flex items-center mt-1">
                      <span className={`font-medium ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{scholarship.amount}</span>
                      <span className={`mx-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>•</span>
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{scholarship.location}</span>
                    </div>
                    <div className={`mt-2 text-sm ${darkMode ? 'text-red-400' : 'text-red-500'}`}>
                      Deadline: {scholarship.deadline}
                    </div>
                  </div>
                  <button className={`px-3 py-1 rounded-md text-sm ${
                    darkMode 
                      ? 'bg-gray-600 hover:bg-gray-500' 
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}>
                    Save
                  </button>
                </div>
                <div className="mt-3">
                  <div className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Eligibility:</div>
                  <div className="flex flex-wrap gap-2">
                    {scholarship.eligibility.map((criteria, i) => (
                      <span key={i} className={`px-2 py-1 rounded-full text-xs ${
                        darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {criteria}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button className={`px-4 py-2 rounded-md text-sm ${
                    darkMode 
                      ? 'bg-indigo-600 hover:bg-indigo-700' 
                      : 'bg-indigo-500 hover:bg-indigo-600'
                  } text-white`}>
                    Apply Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const GamificationTab = ({ darkMode }) => {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Gamification & Recognition</h1>
      
      {/* Leaderboard Section */}
      <div className={`rounded-lg shadow-md overflow-hidden mb-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="font-semibold text-lg">Reputation & Badges System</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className={`p-4 rounded-lg text-center ${darkMode ? 'bg-yellow-900 bg-opacity-20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-100'}`}>
              <div className="text-yellow-500 mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9                  10.29C17.176 19 21 14.291 21 9c0-1.315-.272-2.554-.618-3.016z" />
                </svg>
              </div>
              <h3 className="font-medium text-lg mb-1">Badge Champion</h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Earned by collecting 20 badges.
              </p>
            </div>
            <div className={`p-4 rounded-lg text-center ${darkMode ? 'bg-blue-900 bg-opacity-20 border border-blue-800' : 'bg-blue-50 border border-blue-100'}`}>
              <div className="text-blue-500 mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v9.254a3 3 0 00-2.528 2.744L3 21h18l-6.472-5.002A3 3 0 0012 12.254V3z" />
                </svg>
              </div>
              <h3 className="font-medium text-lg mb-1">Top Contributor</h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Earned by providing 50 answers in the community.
              </p>
            </div>
            <div className={`p-4 rounded-lg text-center ${darkMode ? 'bg-green-900 bg-opacity-20 border border-green-800' : 'bg-green-50 border border-green-100'}`}>
              <div className="text-green-500 mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.34a9.975 9.975 0 00-.377-1.342c-.15-.317-.328-.628-.58-.977-1.12-1.51-3.657-3.390-6.469-3.393H9m6.747 2.556a9.916 9.916 0 01-1.322 5.26m-4.42 0c1.832 0 3.528-1.612 3.528-4.359a1.78 1.78 0 00-.033-.24" />
                </svg>
              </div>
              <h3 className="font-medium text-lg mb-1">Community Star</h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Earned by achieving the most upvotes in discussions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Gamification Hub Section */}
      <div className={`rounded-lg shadow-md overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="font-semibold text-lg">Your Points and Rewards</h2>
        </div>
        <div className="p-4">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-medium text-lg">Total Points: 1200</h3>
            <button className={`px-4 py-2 rounded-md ${darkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'} text-white`}>
              Redeem Rewards
            </button>
          </div>
          <ul className="space-y-2">
            {[
              { name: "5% Discount on Course Enrollment", value: 500 },
              { name: "Free E-Book for Top Performers", value: 300 },
              { name: "Exclusive Webinar Access", value: 200 },
            ].map((reward, index) => (
              <li key={index} className={`flex items-center justify-between py-2 rounded-md ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <span className="font-medium">{reward.name}</span>
                <span>{reward.value} points</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// Export the main App component
export default App;
